import { useState, useEffect } from "react";
import {
	Paper,
	Button,
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
} from '@mui/material';
import SectionTitle from '../../../components/common/SectionTitle'
import ClientService from '../../../services/ClientService'
import TipContext from '../../../providers/GlobalTip'

function extractApiErrorMessage(error, fallbackMessage) {
	const responseData = error?.response?.data

	if (typeof responseData?.message === 'string' && responseData.message.trim() !== '') {
		return responseData.message.trim()
	}

	if (typeof responseData?.error === 'string' && responseData.error.trim() !== '') {
		return responseData.error.trim()
	}

	if (typeof responseData === 'string' && responseData.trim() !== '') {
		return responseData.trim()
	}

	if (typeof error?.message === 'string' && error.message.trim() !== '') {
		return error.message.trim()
	}

	return fallbackMessage
}

function isClientAiForecastEnabled(client) {
	if (typeof client?.featureFlagsMap === 'object' && client?.featureFlagsMap !== null) {
		return !!client.featureFlagsMap['weekly-plan-ui']
	}

	if (typeof client?.featureFlags === 'object' && client?.featureFlags !== null && !Array.isArray(client.featureFlags)) {
		return !!client.featureFlags['weekly-plan-ui']
	}

	return false
}

export function getLocationAiForecastState(item, client) {
	if (!isClientAiForecastEnabled(client)) {
		return {
			eligible: false,
			eligibleCount: 0,
			reason: 'AI forecast is not enabled for this client.',
		}
	}

	const aiForecast = item?.ai_forecast || {}
	const eligibleCount = Number(aiForecast.eligible_count || 0)
	const eligible = !!aiForecast.eligible || eligibleCount > 0
	const reason = aiForecast.reason || (eligible ? null : 'AI forecast is not enabled for this location.')

	return {
		eligible,
		eligibleCount,
		reason,
	}
}

function ClientLocation(props) {
	const { client, isAdmin, onClientAiForecastConflict } = props
	const [data, setData] = useState([])
	const [triggeringLocationId, setTriggeringLocationId] = useState(null)
	const tip = TipContext.useMessage()
	const clientBatchStatus = client?.ai_forecast?.batch_status
	const clientRunActive = clientBatchStatus === 'queued' || clientBatchStatus === 'pending'

	useEffect(() => {
		if( client ) {
			loadStatistics()
		}
	}, [client])

	async function loadStatistics() {
		const data = await ClientService.getClientLocationClassStatistics(client.Id)
		setData(data)
	}

	async function triggerLocationAiForecast(location, aiForecastState) {
		if (!aiForecastState?.eligible) {
			tip.clear()
			tip.show(aiForecastState?.reason || `Unable to queue AI forecast for ${location.location_name}.`, 5)
			return
		}

		if (clientRunActive) {
			tip.clear()
			tip.show(
				client?.ai_forecast?.batch_reason || 'AI forecast is already running for this client. Wait for the current request to finish before submitting another run.',
				5,
			)
			return
		}

		if (!window.confirm(`Are you sure you want to queue AI forecast generation for ${location.location_name}? Updated planning data will appear after processing completes.`)) {
			return
		}

		setTriggeringLocationId(location.client_location_id)
		tip.showSuccess(`Queueing AI forecast for ${location.location_name}...`)

		try {
			const response = await ClientService.triggerLocationAiForecast(client.Id, location.client_location_id)
			tip.showSuccess(response?.message || `AI forecast has been queued for ${location.location_name}. Updated planning data will appear after processing completes.`)
		} catch (e) {
			console.log(e)
			const errorMessage = extractApiErrorMessage(e, `Unable to queue AI forecast for ${location.location_name}.`)
			if (e?.response?.status === 409 && typeof onClientAiForecastConflict === 'function') {
				onClientAiForecastConflict(e.response?.data || {})
			}
			tip.clear()
			tip.show(errorMessage, 5)
		} finally {
			setTriggeringLocationId(null)
		}
	}

	return <Paper elevation={2}>
		<SectionTitle 
			label="Locations"
			rightComponent={
				isAdmin ? <Button size="small">
					Manage Locations
				</Button>
				: null
			}
		/>
		<TableContainer sx={{py:2}}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Name</TableCell>
						{isAdmin ? <TableCell align="center">AI Forecast</TableCell> : null}
						<TableCell align="right">OTB</TableCell>
						<TableCell align="right">PNOTB</TableCell>
						<TableCell align="right">NOTB</TableCell>
						<TableCell align="right">Location</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
				{ data ? data.map((item, index) => {
					const aiForecastState = getLocationAiForecastState(item, client)
					const disabledReason = clientRunActive
						? (client?.ai_forecast?.batch_reason || 'AI forecast is already running for this client. Wait for the current request to finish before submitting another run.')
						: aiForecastState.reason
					return <TableRow key={`location_statistics_${index}`}>
						<TableCell>{item.location.location_name}</TableCell>
						{isAdmin ? <TableCell align="center">
							<span title={disabledReason || ''}>
								<Button
									variant="contained"
									size="small"
									onClick={() => triggerLocationAiForecast(item.location, aiForecastState)}
									disabled={!client || !aiForecastState.eligible || clientRunActive || triggeringLocationId === item.location.client_location_id}
								>
									{triggeringLocationId === item.location.client_location_id ? 'Queueing...' : 'AI Forecast'}
								</Button>
							</span>
						</TableCell> : null}
						<TableCell align="right">{item.count?.OTB ? item.count.OTB : 0}</TableCell>
						<TableCell align="right">{item.count?.PNOTB ? item.count.PNOTB : 0}</TableCell>
						<TableCell align="right">{item.count?.NOTB ? item.count.NOTB : 0}</TableCell>
						<TableCell align="right">{item.location.city ? item.location.city+', ' : ''} {item.location.state ? item.location.state+', ' : ''}</TableCell>
					</TableRow>
				}) : null}
				</TableBody>
			</Table>
		</TableContainer>
	</Paper>
}
export default ClientLocation